import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { API_BASE_URL, apiCall } from '../utils/api'
import LoadingSpinner from './LoadingSpinner'
import { UserIcon, CheckCircleIcon, BriefcaseIcon } from './Icons'
import Cropper from 'react-easy-crop'
import type { Area } from 'react-easy-crop'

interface OnboardingModalProps {
  onComplete: () => void
}

interface ProfileData {
  firstName: string
  lastName: string
  email: string
  phone: string
  profilePictureUrl: string | null
  displayFirstName: string
  displayLastName: string
  displayEmail: string
  displayPhone: string
  brokerageFirmName: string
  isTrainee: boolean
}

const OnboardingModal: React.FC<OnboardingModalProps> = ({ onComplete }) => {
  const { t } = useTranslation()
  const { user, registerPasskey, supportsPasskeys } = useAuth()
  
  const [currentStep, setCurrentStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Profile data
  const [profileData, setProfileData] = useState<ProfileData>({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    profilePictureUrl: null,
    // Display fields - auto-populate from account data
    displayFirstName: user?.firstName || '',
    displayLastName: user?.lastName || '',
    displayEmail: user?.email || '',
    displayPhone: user?.phone || '',
    brokerageFirmName: '',
    isTrainee: false
  })
  
  // Security setup
  const [setupMethod, setSetupMethod] = useState<'passkey' | '2fa' | 'sms2fa' | null>(null)
  const [tfaSecret, setTfaSecret] = useState('')
  const [tfaQRCode, setTfaQRCode] = useState('')
  const [tfaToken, setTfaToken] = useState('')
  const [passkeyDeviceName, setPasskeyDeviceName] = useState('')
  
  // SMS 2FA state
  const [smsPhoneNumber, setSmsPhoneNumber] = useState(profileData.phone)
  const [smsCode, setSmsCode] = useState('')
  const [smsCodeSent, setSmsCodeSent] = useState(false)
  const [smsResendTimer, setSmsResendTimer] = useState(0)
  
  // Profile picture upload
  const [uploadingPicture, setUploadingPicture] = useState(false)
  const [pictureFile, setPictureFile] = useState<File | null>(null)
  const [picturePreview, setPicturePreview] = useState<string | null>(null)
  
  // Photo cropper state
  const [showCropper, setShowCropper] = useState(false)
  const [cropperImage, setCropperImage] = useState<string | null>(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [imageCropSettings, setImageCropSettings] = useState<{crop: {x: number, y: number}, zoom: number, croppedArea: Area} | null>(null)
  
  // Transition state
  const [isTransitioning, setIsTransitioning] = useState(false)

  // Auto-trigger 2FA setup when method is selected
  useEffect(() => {
    if (setupMethod === '2fa' && !tfaSecret && !loading) {
      setup2FA()
    }
  }, [setupMethod, tfaSecret, loading])

  // Crop complete callback
  const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])

  // Create cropped image
  const createCroppedImage = async (imageSrc: string, pixelCrop: Area): Promise<Blob> => {
    const image = await createImage(imageSrc)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      throw new Error('No 2d context')
    }

    canvas.width = pixelCrop.width
    canvas.height = pixelCrop.height

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    )

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('Canvas is empty'))
        }
      }, 'image/jpeg', 0.95)
    })
  }

  // Helper to load image
  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image()
      image.addEventListener('load', () => resolve(image))
      image.addEventListener('error', (error) => reject(error))
      image.src = url
    })

  // Apply crop and close modal
  const applyCrop = async () => {
    if (!cropperImage || !croppedAreaPixels) return

    try {
      const croppedBlob = await createCroppedImage(cropperImage, croppedAreaPixels)
      const croppedFile = new File([croppedBlob], 'profile.jpg', { type: 'image/jpeg' })
      
      setPictureFile(croppedFile)
      
      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setPicturePreview(reader.result as string)
      }
      reader.readAsDataURL(croppedFile)
      
      // Save crop settings
      setImageCropSettings({
        crop,
        zoom,
        croppedArea: croppedAreaPixels
      })
      
      setShowCropper(false)
      setCropperImage(null)
    } catch (error) {
      console.error('Error cropping image:', error)
      setError('Failed to crop image')
    }
  }

  const steps = [
    {
      id: 'welcome',
      titleKey: 'onboarding.welcome.title',
      descriptionKey: 'onboarding.welcome.description',
      icon: <CheckCircleIcon size={24} className="text-gray-900 dark:text-gray-600" />
    },
    {
      id: 'profile',
      titleKey: 'onboarding.profile.title',
      descriptionKey: 'onboarding.profile.description',
      icon: <UserIcon size={24} className="text-gray-900 dark:text-gray-600" />
    },
    {
      id: 'security',
      titleKey: 'onboarding.security.title',
      descriptionKey: 'onboarding.security.description',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-900 dark:text-gray-600">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
      )
    },
    {
      id: 'trial',
      titleKey: 'onboarding.trial.title',
      descriptionKey: 'onboarding.trial.description',
      icon: <BriefcaseIcon size={24} className="text-gray-900 dark:text-gray-600" />
    }
  ]

  const currentStepData = steps[currentStep]

  const handleProfilePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError(t('onboarding.profile.pictureTooLarge'))
        return
      }
      
      // Read file and show cropper
      const reader = new FileReader()
      reader.onloadend = () => {
        setCropperImage(reader.result as string)
        setShowCropper(true)
        // Reset crop settings
        setCrop({ x: 0, y: 0 })
        setZoom(1)
      }
      reader.readAsDataURL(file)
    }
  }

  const uploadProfilePicture = async (): Promise<string | null> => {
    if (!pictureFile) return null
    
    try {
      setUploadingPicture(true)
      const formData = new FormData()
      formData.append('profilePicture', pictureFile)
      
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/users/profile-picture`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      })
      
      if (!response.ok) {
        throw new Error('Failed to upload picture')
      }
      
      const data = await response.json()
      return data.profile_picture_url
    } catch (err) {
      console.error('Error uploading profile picture:', err)
      return null
    } finally {
      setUploadingPicture(false)
    }
  }

  const saveProfile = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Upload profile picture if one was selected
      let pictureUrl = profileData.profilePictureUrl
      if (pictureFile) {
        pictureUrl = await uploadProfilePicture()
      }
      
      // Build update payload
      const payload: any = {
        first_name: profileData.firstName,
        last_name: profileData.lastName,
        is_trainee: profileData.isTrainee
      }
      
      // Only include optional fields if they have values
      if (profileData.phone) payload.phone = profileData.phone
      if (profileData.displayFirstName) payload.display_first_name = profileData.displayFirstName
      if (profileData.displayLastName) payload.display_last_name = profileData.displayLastName
      if (profileData.displayEmail) payload.display_email = profileData.displayEmail
      if (profileData.displayPhone) payload.display_phone = profileData.displayPhone
      if (profileData.brokerageFirmName) payload.brokerage_firm_name = profileData.brokerageFirmName
      
      // Only include profile_picture_url if a picture was set
      if (pictureUrl) {
        payload.profile_picture_url = pictureUrl
      }
      
      // Include crop settings if available
      if (imageCropSettings) {
        payload.profile_picture_crop_settings = imageCropSettings
      }
      
      // Update profile
      const response = await apiCall('/api/users/onboarding-profile', {
        method: 'PUT',
        body: JSON.stringify(payload)
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        console.error('Save error:', errorData)
        throw new Error(errorData.message || 'Failed to update profile')
      }
      
      return true
    } catch (err) {
      console.error('Error saving profile:', err)
      setError(t('onboarding.profile.saveFailed'))
      return false
    } finally {
      setLoading(false)
    }
  }

  const setup2FA = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Get 2FA setup
      const setupResponse = await apiCall('/api/2fa/setup/begin', {
        method: 'POST'
      })
      if (!setupResponse.ok) {
        throw new Error('Failed to setup 2FA')
      }
      
      const setupData = await setupResponse.json()
      setTfaSecret(setupData.secret)
      setTfaQRCode(setupData.qrCode)
      return true
    } catch (err) {
      console.error('Error setting up 2FA:', err)
      setError(t('security.setupFailed'))
      return false
    } finally {
      setLoading(false)
    }
  }

  const verify2FA = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await apiCall('/api/2fa/setup/verify', {
        method: 'POST',
        body: JSON.stringify({ token: tfaToken })
      })
      
      if (!response.ok) {
        throw new Error('Invalid 2FA token')
      }
      
      return true
    } catch (err) {
      console.error('Error verifying 2FA:', err)
      setError(t('security.setupFailed'))
      return false
    } finally {
      setLoading(false)
    }
  }

  const setupPasskey = async () => {
    try {
      setLoading(true)
      setError(null)
      
      await registerPasskey(passkeyDeviceName || t('onboarding.security.defaultPasskeyName'))
      return true
    } catch (err) {
      console.error('Error setting up passkey:', err)
      setError(t('security.setupFailed'))
      return false
    } finally {
      setLoading(false)
    }
  }

  const sendSmsCode = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Validate phone number
      const phoneRegex = /^\+?[1-9]\d{1,14}$/
      const cleanPhone = smsPhoneNumber.replace(/\s/g, '')
      if (!phoneRegex.test(cleanPhone)) {
        setError(t('onboarding.security.invalidPhoneFormat'))
        return false
      }
      
      const response = await apiCall('/api/sms2fa/setup/send-code', {
        method: 'POST',
        body: JSON.stringify({ phone: cleanPhone })
      })
      
      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to send code')
      }
      
      setSmsCodeSent(true)
      // Start 60 second timer
      setSmsResendTimer(60)
      const interval = setInterval(() => {
        setSmsResendTimer(prev => {
          if (prev <= 1) {
            clearInterval(interval)
            return 0
          }
          return prev - 1
        })
      }, 1000)
      
      return true
    } catch (err) {
      console.error('Error sending SMS code:', err)
      setError(t('security.setupFailed'))
      return false
    } finally {
      setLoading(false)
    }
  }

  const verifySmsCode = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const cleanPhone = smsPhoneNumber.replace(/\s/g, '')
      const response = await apiCall('/api/sms2fa/setup/verify', {
        method: 'POST',
        body: JSON.stringify({ 
          phone: cleanPhone,
          code: smsCode 
        })
      })
      
      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Invalid code')
      }
      
      return true
    } catch (err) {
      console.error('Error verifying SMS code:', err)
      setError(t('security.setupFailed'))
      return false
    } finally {
      setLoading(false)
    }
  }

  const startTrial = async () => {
    // Trials are now managed through Stripe's Customer Portal.
    // Open the billing portal so the user can set up their subscription/trial.
    try {
      setLoading(true)
      setError(null)

      const token = localStorage.getItem('token')

      // First dismiss the welcome modal so it doesn't show again
      await fetch(`${API_BASE_URL}/api/membership/manage/dismiss-welcome`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      })

      // Then open the Stripe Customer Portal in a new tab
      const portalResponse = await fetch(`${API_BASE_URL}/api/membership/manage/billing-portal`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (portalResponse.ok) {
        const portalData = await portalResponse.json()
        if (portalData.portal_url) {
          window.open(portalData.portal_url, '_blank')
        }
      }

      return true
    } catch (err) {
      console.error('Error opening billing portal:', err)
      setError(t('onboarding.trial.startFailed'))
      return false
    } finally {
      setLoading(false)
    }
  }

  const handleNext = async () => {
    setError(null)
    
    // Handle profile step
    if (currentStep === 1) {
      const success = await saveProfile()
      if (!success) return
    }
    
    // Handle security step - only move forward if a method was selected and set up
    if (currentStep === 2) {
      if (!setupMethod) {
        setError(t('onboarding.security.pleaseSelectMethod'))
        return
      }
      
      if (setupMethod === '2fa') {
        if (!tfaSecret) {
          // Initialize 2FA setup
          const success = await setup2FA()
          if (!success) return
          return // Stay on this step to show QR code
        } else if (!tfaToken) {
          setError(t('onboarding.security.enterToken'))
          return
        } else {
          // Verify 2FA token
          const success = await verify2FA()
          if (!success) return
        }
      } else if (setupMethod === 'passkey') {
        const success = await setupPasskey()
        if (!success) return
      } else if (setupMethod === 'sms2fa') {
        if (!smsCodeSent) {
          // Send SMS code
          const success = await sendSmsCode()
          if (!success) return
          return // Stay on this step to enter code
        } else if (!smsCode) {
          setError(t('onboarding.security.enterSmsCode'))
          return
        } else {
          // Verify SMS code
          const success = await verifySmsCode()
          if (!success) return
        }
      }
    }
    
    // Handle trial step
    if (currentStep === 3) {
      const success = await startTrial()
      if (success) {
        onComplete()
        return
      }
      return
    }
    
    // Move to next step with smooth transition
    if (currentStep < steps.length - 1) {
      setIsTransitioning(true)
      setTimeout(() => {
        setCurrentStep(currentStep + 1)
        setTimeout(() => setIsTransitioning(false), 100)
      }, 200)
    }
  }

  const handleBack = () => {
    if (currentStep > 0) {
      setIsTransitioning(true)
      // Reset security setup state when going back from security step
      if (currentStep === 2) {
        setSetupMethod(null)
        setTfaSecret('')
        setTfaQRCode('')
        setTfaToken('')
        setPasskeyDeviceName('')
        setSmsCode('')
        setSmsCodeSent(false)
        setSmsResendTimer(0)
      }
      setTimeout(() => {
        setCurrentStep(currentStep - 1)
        setError(null)
        setTimeout(() => setIsTransitioning(false), 100)
      }, 200)
    }
  }

  const canProceed = () => {
    if (currentStep === 1) {
      // Business card step - require display name and email
      return profileData.displayFirstName.trim() && profileData.displayLastName.trim() && profileData.displayEmail.trim()
    }
    if (currentStep === 2) {
      // Security step - require method selection and completion
      if (!setupMethod) return false
      if (setupMethod === '2fa' && tfaSecret && !tfaToken) return false
      return true
    }
    return true
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-3xl w-full p-8 transform transition-all min-h-[600px] flex flex-col">
          
          {/* Progress bar */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              {steps.map((step, index) => (
                <div
                  key={step.id}
                  className={`flex items-center ${index < steps.length - 1 ? 'flex-1' : ''}`}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                      index === currentStep
                        ? 'bg-gray-900 text-white scale-110'
                        : index < currentStep
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                    }`}
                  >
                    {index < currentStep ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    ) : (
                      step.icon
                    )}
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`flex-1 h-1 mx-2 transition-all ${
                        index < currentStep ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="text-center text-sm text-gray-500 dark:text-gray-400 mt-2">
              {t('onboarding.stepProgress', { current: currentStep + 1, total: steps.length })}
            </div>
          </div>

          {/* Content */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              {currentStep === 0 
                ? t('onboarding.welcome.personalizedTitle', { name: user?.firstName || 'there' })
                : t(currentStepData.titleKey)
              }
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              {t(currentStepData.descriptionKey)}
            </p>
          </div>

          {/* Step content */}
          <div className={`mb-8 flex-1 transition-opacity duration-200 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
            {/* Welcome step */}
            {currentStep === 0 && (
              <div className="text-center py-8">
                <div className="w-24 h-24 mx-auto mb-8 bg-gradient-to-br from-gray-800 to-gray-900 rounded-full flex items-center justify-center">
                  <svg className="w-12 h-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="space-y-4 max-w-lg mx-auto">
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-left flex items-start space-x-3">
                    <svg className="w-6 h-6 text-gray-900 dark:text-gray-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <div className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                        {t('onboarding.welcome.feature1Title')}
                      </div>
                      <div className="text-sm text-blue-700 dark:text-blue-300">
                        {t('onboarding.welcome.feature1Description')}
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-left flex items-start space-x-3">
                    <svg className="w-6 h-6 text-gray-900 dark:text-gray-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                    </svg>
                    <div>
                      <div className="font-semibold text-purple-900 dark:text-purple-100 mb-1">
                        {t('onboarding.welcome.feature2Title')}
                      </div>
                      <div className="text-sm text-purple-700 dark:text-purple-300">
                        {t('onboarding.welcome.feature2Description')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Public Profile Setup step */}
            {currentStep === 1 && (
              <div className="max-w-xl mx-auto">
                {/* Editable Business Card */}
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-6 border-2 border-blue-200 dark:border-gray-600 shadow-xl">
                  <div className="flex items-start space-x-4">
                    {/* Profile Picture */}
                    <div className="relative flex-shrink-0">
                      {picturePreview ? (
                        <>
                          <img
                            src={picturePreview}
                            alt="Profile"
                            className="w-24 h-24 rounded-full object-cover border-4 border-white dark:border-gray-600 shadow-lg"
                          />
                          <button
                            onClick={() => {
                              setPictureFile(null)
                              setPicturePreview(null)
                              setProfileData({ ...profileData, profilePictureUrl: null })
                            }}
                            className="absolute -top-1 -right-1 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 transition-colors shadow-lg"
                            title="Remove photo"
                          >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </>
                      ) : (
                        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                          {profileData.displayFirstName.charAt(0)}{profileData.displayLastName.charAt(0)}
                        </div>
                      )}
                      <label className="absolute bottom-0 right-0 bg-gray-900 text-white p-2 rounded-full cursor-pointer hover:bg-gray-800 transition-colors shadow-lg">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleProfilePictureChange}
                          className="hidden"
                        />
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </label>
                    </div>
                    
                    {/* Editable Info */}
                    <div className="flex-1 min-w-0 space-y-2">
                      {/* Name */}
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={profileData.displayFirstName}
                          onChange={(e) => setProfileData({ ...profileData, displayFirstName: e.target.value })}
                          placeholder={t('onboarding.profile.firstName')}
                          className="flex-1 min-w-0 text-lg font-bold bg-white/50 dark:bg-gray-900/50 border-2 border-transparent focus:border-gray-900 rounded-lg px-2 py-1 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none transition-colors"
                          required
                        />
                        <input
                          type="text"
                          value={profileData.displayLastName}
                          onChange={(e) => setProfileData({ ...profileData, displayLastName: e.target.value })}
                          placeholder={t('onboarding.profile.lastName')}
                          className="flex-1 min-w-0 text-lg font-bold bg-white/50 dark:bg-gray-900/50 border-2 border-transparent focus:border-gray-900 rounded-lg px-2 py-1 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none transition-colors"
                          required
                        />
                      </div>

                      {/* Professional Title */}
                      <div className="text-xs font-medium text-gray-700 dark:text-gray-300">
                        {profileData.isTrainee ? t('onboarding.profile.traineeTitle') : t('onboarding.profile.professionalTitle')}
                      </div>

                      {/* Brokerage Firm */}
                      <input
                        type="text"
                        value={profileData.brokerageFirmName}
                        onChange={(e) => setProfileData({ ...profileData, brokerageFirmName: e.target.value })}
                        placeholder={t('onboarding.profile.brokerageFirmPlaceholder')}
                        className="w-full bg-white/50 dark:bg-gray-900/50 border-2 border-transparent focus:border-gray-900 rounded-lg px-2 py-1 text-xs text-gray-700 dark:text-gray-300 placeholder-gray-400 focus:outline-none transition-colors"
                      />

                      {/* Divider */}
                      <div className="border-t border-gray-300 dark:border-gray-600 my-2"></div>

                      {/* Contact Info */}
                      <div className="space-y-1.5">
                        <div className="flex items-center space-x-2">
                          <svg className="w-4 h-4 text-gray-600 dark:text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <input
                            type="email"
                            value={profileData.displayEmail}
                            onChange={(e) => setProfileData({ ...profileData, displayEmail: e.target.value })}
                            placeholder="email@example.com"
                            className="flex-1 min-w-0 bg-white/50 dark:bg-gray-900/50 border-2 border-transparent focus:border-gray-900 rounded-lg px-2 py-1 text-xs text-gray-700 dark:text-gray-300 placeholder-gray-400 focus:outline-none transition-colors"
                            required
                          />
                        </div>

                        <div className="flex items-center space-x-2">
                          <svg className="w-4 h-4 text-gray-600 dark:text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          <input
                            type="tel"
                            value={profileData.displayPhone}
                            onChange={(e) => setProfileData({ ...profileData, displayPhone: e.target.value })}
                            placeholder="+1 416 555 1234"
                            className="flex-1 min-w-0 bg-white/50 dark:bg-gray-900/50 border-2 border-transparent focus:border-gray-900 rounded-lg px-2 py-1 text-xs text-gray-700 dark:text-gray-300 placeholder-gray-400 focus:outline-none transition-colors"
                          />
                        </div>
                      </div>

                      {/* Trainee Checkbox */}
                      <div className="flex items-center space-x-2 mt-2 pt-2 border-t border-gray-300 dark:border-gray-600">
                        <input
                          type="checkbox"
                          id="isTrainee"
                          checked={profileData.isTrainee}
                          onChange={(e) => setProfileData({ ...profileData, isTrainee: e.target.checked })}
                          className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-2 focus:ring-gray-900"
                        />
                        <label htmlFor="isTrainee" className="text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
                          {t('onboarding.profile.isTrainee')}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Security step */}
            {currentStep === 2 && (
              <div className="space-y-6 max-w-xl mx-auto">
                {!setupMethod ? (
                  // Method selection
                  <div className="space-y-4">
                    {supportsPasskeys && (
                      <button
                        onClick={() => setSetupMethod('passkey')}
                        className="w-full p-6 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-gray-900 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all text-left group"
                      >
                        <div className="flex items-center space-x-4">
                          <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                            <svg className="w-8 h-8 text-gray-900 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold text-lg text-gray-900 dark:text-white group-hover:text-gray-900 dark:group-hover:text-gray-600">
                              {t('security.passkeyRecommended')}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {t('security.passkeySetupDescription')}
                            </div>
                          </div>
                          <svg className="w-6 h-6 text-gray-900 dark:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </button>
                    )}
                    
                    <button
                      onClick={() => setSetupMethod('2fa')}
                      className="w-full p-6 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-gray-900 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all text-left group"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                          <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-lg text-gray-900 dark:text-white group-hover:text-gray-900 dark:group-hover:text-gray-600">
                            {t('security.authenticatorApp')}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {t('security.authenticatorDescription')}
                          </div>
                        </div>
                        <svg className="w-6 h-6 text-gray-900 dark:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => {
                        setSetupMethod('sms2fa')
                        setSmsPhoneNumber(profileData.phone)
                      }}
                      className="w-full p-6 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-gray-900 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all text-left group"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                          <svg className="w-8 h-8 text-gray-900 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-lg text-gray-900 dark:text-white group-hover:text-gray-900 dark:group-hover:text-gray-600">
                            {t('security.smsAuthentication')}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {t('security.smsDescription')}
                          </div>
                        </div>
                        <svg className="w-6 h-6 text-gray-900 dark:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                  </div>
                ) : setupMethod === 'passkey' ? (
                  // Passkey setup
                  <div className="text-center space-y-6 min-h-[300px]">
                    <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-gray-800 to-gray-900 rounded-full flex items-center justify-center">
                      <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                      </svg>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('onboarding.security.deviceName')}
                      </label>
                      <input
                        type="text"
                        value={passkeyDeviceName}
                        onChange={(e) => setPasskeyDeviceName(e.target.value)}
                        placeholder={t('onboarding.security.deviceNamePlaceholder')}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-gray-900 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('onboarding.security.passkeyInstructions')}
                    </p>
                  </div>
                ) : setupMethod === '2fa' && !tfaSecret ? (
                  // Initializing 2FA
                  <div className="text-center py-8 min-h-[400px] flex items-center justify-center">
                    <LoadingSpinner />
                  </div>
                ) : setupMethod === '2fa' && tfaSecret ? (
                  // 2FA setup with QR code
                  <div className="space-y-6 min-h-[400px]">
                    <div className="text-center">
                      <div className="inline-block p-4 bg-white rounded-lg">
                        <img src={tfaQRCode} alt="QR Code" className="w-64 h-64" />
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
                        {t('security.scanQRCode')}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('security.enterCodeFromApp')}
                      </label>
                      <input
                        type="text"
                        value={tfaToken}
                        onChange={(e) => setTfaToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        className="w-full px-4 py-2 text-center text-2xl tracking-widest border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-gray-900 dark:bg-gray-700 dark:text-white"
                        maxLength={6}
                      />
                    </div>
                  </div>
                ) : setupMethod === 'sms2fa' ? (
                  // SMS 2FA setup
                  <div className="space-y-6 min-h-[350px]">
                    <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-gray-800 to-gray-900 rounded-full flex items-center justify-center">
                      <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                    </div>
                    {!smsCodeSent ? (
                      // Phone number input
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('securityFeatureCards.phoneNumber')}
                        </label>
                        <input
                          type="tel"
                          value={smsPhoneNumber}
                          onChange={(e) => setSmsPhoneNumber(e.target.value)}
                          placeholder={t('securityFeatureCards.phoneNumberPlaceholder')}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-gray-900 dark:bg-gray-700 dark:text-white"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {t('securityFeatureCards.phoneNumberHelper')}
                        </p>
                      </div>
                    ) : (
                      // Code verification
                      <div className="space-y-4">
                        <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                          <p className="text-sm text-green-800 dark:text-green-200">
                            {t('securityFeatureCards.verificationCodeSent')}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {t('security.enterSMSCode')}
                          </label>
                          <input
                            type="text"
                            value={smsCode}
                            onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            placeholder="000000"
                            className="w-full px-4 py-2 text-center text-2xl tracking-widest border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-gray-900 dark:bg-gray-700 dark:text-white"
                            maxLength={6}
                            autoFocus
                          />
                        </div>
                        <div className="text-center">
                          <button
                            onClick={sendSmsCode}
                            disabled={smsResendTimer > 0 || loading}
                            className="text-sm text-gray-900 dark:text-gray-600 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {smsResendTimer > 0 
                              ? t('security.resendIn', { seconds: smsResendTimer })
                              : t('security.resendCode')
                            }
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            )}

            {/* Trial step */}
            {currentStep === 3 && (
              <div className="text-center py-8">
                <div className="w-24 h-24 mx-auto mb-8 bg-gradient-to-br from-gray-800 to-gray-900 rounded-full flex items-center justify-center">
                  <svg className="w-12 h-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <div className="space-y-4 max-w-lg mx-auto">
                  <div className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl">
                    <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-900 mb-2">
                      {t('onboarding.trial.freeTrialTitle')}
                    </div>
                    <div className="text-gray-700 dark:text-gray-300">
                      {t('onboarding.trial.freeTrialDescription')}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 mt-6">
                    <div className="p-4 bg-white dark:bg-gray-700 rounded-lg shadow">
                      <div className="w-10 h-10 mx-auto mb-2 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">
                        {t('welcome.features.fullAccess.title')}
                      </div>
                    </div>
                    <div className="p-4 bg-white dark:bg-gray-700 rounded-lg shadow">
                      <div className="w-10 h-10 mx-auto mb-2 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-gray-900 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 10l2 2 4-4" />
                        </svg>
                      </div>
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">
                        {t('welcome.features.noPayment.title')}
                      </div>
                    </div>
                    <div className="p-4 bg-white dark:bg-gray-700 rounded-lg shadow">
                      <div className="w-10 h-10 mx-auto mb-2 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-gray-900 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">
                        {t('welcome.features.thirtyDays.title')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-red-800 dark:text-red-300 text-sm">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-between space-x-4">
            {currentStep > 0 && (
              <button
                onClick={handleBack}
                disabled={loading}
                className="px-6 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-semibold rounded-lg transition-colors disabled:opacity-50"
              >
                {t('onboarding.actions.back')}
              </button>
            )}
            
            <button
              onClick={handleNext}
              disabled={loading || !canProceed()}
              className="ml-auto px-8 py-3 bg-gradient-to-r from-gray-900 to-gray-900 hover:from-blue-700 hover:to-purple-700 text-white font-bold rounded-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:hover:scale-100 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading && <LoadingSpinner />}
              <span>
                {currentStep === steps.length - 1
                  ? t('onboarding.actions.startTrial')
                  : t('onboarding.actions.continue')}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Photo Cropper Modal */}
      {showCropper && cropperImage && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('onboarding.profile.cropPhoto') || 'Crop & Adjust Photo'}
              </h3>
            </div>

            {/* Cropper Area */}
            <div className="relative" style={{ height: '400px' }}>
              <Cropper
                image={cropperImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>

            {/* Zoom Control */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-4">
                <svg className="w-5 h-5 text-gray-600 dark:text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                </svg>
                <input
                  type="range"
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="flex-1"
                />
                <svg className="w-6 h-6 text-gray-600 dark:text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                </svg>
              </div>
            </div>

            {/* Actions */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCropper(false)
                  setCropperImage(null)
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {t('onboarding.actions.cancel') || 'Cancel'}
              </button>
              <button
                onClick={applyCrop}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors"
              >
                {t('onboarding.actions.apply') || 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default OnboardingModal

